import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { StorageModel } from 'src/app/models/storage.model';
import { ChunkModel, CreatedFolder, FileEncryptionOptions, UploadInitModel, UploadModel, UploadStoreModel } from 'src/app/models/upload.model';
import { MyStorageService } from 'src/app/pages/storage/storage.service';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';

import { CommonService } from './common.service';
import { FileUploadService } from './file-upload.service';
import { NotificationService } from './notification.service';
@Injectable({
  providedIn: 'root',
})
export class UploadService implements OnDestroy {
  subscription: Subscription = new Subscription();
  dataSubscription: Subscription = new Subscription();
  uploadSubscriptions: { [uploadId: string]: Subscription } = {};
  completeSubscription: Subscription = new Subscription();
  uploadFiles: UploadModel[] = [];
  functionTrigger$: Subject<void> = new Subject<void>();
  hasProcessedDrop$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  uploadItems$: BehaviorSubject<UploadModel[]> = new BehaviorSubject<UploadModel[]>([]);

  encryptedParentId!: string | null;
  objectId!: string | null;
  uploadCompleted$: Subject<boolean> = new Subject<boolean>();
  isEncrypted = false;
  ongoingUpload = false;
  isUploadsCancelled = false;
  private readonly NUMBER_OF_CHUNKS = 4;
  private readonly CHUNK_SIZE = 5 * 1024 * 1024; // Chunk size in MB
  parentId!: string;

  filesAndFolders: StorageModel[] = [];
  existingFile!: StorageModel;

  private encryptedId = new Subject<string | null>();
  encryptedId$ = this.encryptedId.asObservable();

  private objectIdSubject = new Subject<string | null>();
  objectId$ = this.objectIdSubject.asObservable();

  constructor(
    private fileUploadService: FileUploadService,
    private myStorageService: MyStorageService,
    private notificationService: NotificationService,
    private commonService: CommonService,
  ) {
    this.encryptedId$.subscribe((id) => {
      this.isEncrypted = !!id || id === 'root';
      this.encryptedParentId = id && id !== 'root' ? id : null;
    });
    this.objectId$.subscribe((id) => {
      this.objectId = id ?? null;
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.uploadFiles.forEach((file) => {
      if (file.status === 'completed') {
        this.unsubscribeUpload(file.uploadId);
      }
    });
    if (this.completeSubscription) {
      this.completeSubscription.unsubscribe();
    }
  }

  groupByRelativePath(files: FileList): any {
    return Array.from(files).reduce((groups: any, file: File) => {
      const pathParts = file.webkitRelativePath.split('/');
      const folderPath = pathParts.slice(0, pathParts.length - 1).join('/');
      const group = groups[folderPath] || [];
      group.push(file);

      return {
        ...groups,
        [folderPath]: group,
      };
    }, {});
  }

  async uploadFolder(files: FileList): Promise<void> {
    this.isUploadsCancelled = false;
    let parentId: string | null = this.objectId;
    const createdFolders: CreatedFolder[] = [];
    let { encryptedParentId } = this;
    let uploadFile: UploadModel = {} as UploadModel;
    const groupedFiles = this.groupByRelativePath(files);
    for (const key of Object.keys(groupedFiles)) {
      if (this.isUploadsCancelled) {
        break;
      }
      const existingFolder = await createdFolders.find((x) => x.name === key.split('/')[key.split('/').length - 2]);
      const folderName = key.split('/')[key.split('/').length - 1];
      if (existingFolder) {
        parentId = existingFolder.id;
        encryptedParentId = existingFolder.encryptedId;
      }
      await this.myStorageService.triggerCreatingFolder(folderName, this.isEncrypted, parentId, encryptedParentId).then(async (res) => {
        if (this.objectId === parentId) {
          this.functionTrigger$.next();
        }
        createdFolders.push({ id: res.id, name: folderName, encryptedId: res.encryptedId });
        if (createdFolders.length === 0 || !existingFolder) {
          parentId = res.id; // Update `parentId` within the resolved promise
          encryptedParentId = res.parentEncryptedId;
        }
        groupedFiles[key] = groupedFiles[key].filter((file: any) => file.name !== '.DS_Store');
        for await (const item of groupedFiles[key]) {
          if (this.isUploadsCancelled) {
            break;
          }
          const file = item;
          uploadFile = { uploadId: '', objectId: res.id, encryptedParentId: res.encryptedId, file, fileName: file.name, status: 'pending', data: [] };
          this.uploadFiles.push(uploadFile);
          this.uploadInit();
        }
      });
    }
  }

  resetEncryptionState(): void {
    // Reset the encryption-related state
    this.isEncrypted = false;
    this.encryptedParentId = null;

    // Notify any subscriptions if necessary
    this.encryptedId.next(null);
  }

  uploadFile(files: FileList): void {
    if (!this.isEncrypted) {
      this.resetEncryptionState();
    }
    let uploadFile: UploadModel = {} as UploadModel;
    for (let i = 0; i < files.length; i++) {
      uploadFile = { uploadId: '', objectId: this.objectId, encryptedParentId: null, file: files[i], fileName: files[i].name, status: 'pending', data: [] };
      this.uploadFiles.push(uploadFile);
      if (i === 0) {
        this.uploadInit();
      }
    }
  }

  // Start upload
  uploadFolderAndFile(items: DataTransferItemList): void {
    const itemsArray = Array.from(items);
    for (const item of itemsArray) {
      if (this.isUploadsCancelled) {
        break;
      }
      this.startUpload(item);
    }
  }

  startUpload(item: DataTransferItem): void {
    this.isUploadsCancelled = false;
    let parentId: string | null = this.objectId;
    let { encryptedParentId } = this;
    let encryptedId: string | null = null;
    let uploadFile: UploadModel = {} as UploadModel;
    if (item.kind === 'file') {
      const entry = item.webkitGetAsEntry();
      if (entry) {
        if (!this.isUploadsCancelled) {
          if (entry.isFile) {
            this.parseFileEntry(entry).then((file) => {
              uploadFile = { uploadId: '', objectId: parentId, encryptedParentId: null, file, fileName: file.name, status: 'pending', data: [] };
              this.uploadFiles.push(uploadFile);
              this.uploadInit();
            });
          } else if (entry.isDirectory) {
            this.myStorageService.triggerCreatingFolder(entry.name, this.isEncrypted, parentId, encryptedParentId).then((res) => {
              if (this.objectId === parentId) {
                this.functionTrigger$.next();
              }
              parentId = res.id;
              encryptedParentId = res.parentEncryptedId;
              encryptedId = res.encryptedId;
              this.uploadDirectory(parentId, encryptedParentId, encryptedId, entry);
            });
          }
        }
      }
    }
  }

  private uploadDirectory(parentId: string, encryptedParentId: string | null, encryptedId: string | null, entry: any): any {
    let uploadFile: UploadModel = {} as UploadModel;
    this.parseDirectoryEntry(entry).then(async (entries) => {
      entries.map((entry: any) => {
        if (entry.isFile && entry.name !== '.DS_Store') {
          this.parseFileEntry(entry).then((file) => {
            uploadFile = { uploadId: '', objectId: parentId, encryptedParentId: encryptedId, file, fileName: file.name, status: 'pending', data: [] };
            this.uploadFiles.push(uploadFile);
            this.uploadInit();
          });
        } else if (entry.isDirectory) {
          this.myStorageService.triggerCreatingFolder(entry.name, this.isEncrypted, parentId, encryptedParentId).then((res) => {
            if (this.objectId === parentId) {
              this.functionTrigger$.next();
            }
            parentId = res.id;
            encryptedParentId = res.parentEncryptedId;
            encryptedId = res.encryptedId;
            this.uploadDirectory(parentId, encryptedParentId, encryptedId, entry);
          });
        }
      });
    });
  }

  checkIfUploadOnGoing(): UploadModel | undefined {
    if (this.uploadFiles.length > 0) {
      this.ongoingUpload = this.uploadFiles.some((x) => x.status === 'uploading');
      if (!this.ongoingUpload) {
        const file = this.uploadFiles.filter((x) => x.status === 'pending')[0];
        if (file) {
          file.status = 'uploading';

          return file;
        }
      }
    }

    return undefined;
  }

  private parseFileEntry(fileEntry: any): Promise<any> {
    return new Promise((resolve, reject) => {
      fileEntry.file(
        (file: any) => {
          resolve(file);
        },
        (err: any) => {
          reject(err);
        },
      );
    });
  }

  private parseDirectoryEntry(directoryEntry: any): Promise<any> {
    const directoryReader = directoryEntry.createReader();

    return new Promise((resolve, reject) => {
      directoryReader.readEntries(
        (entries: any) => {
          resolve(entries);
        },
        (err: any) => {
          reject(err);
        },
      );
    });
  }

  uploadInit(): void {
    this.uploadItems$.next(this.uploadFiles);
    this.hasProcessedDrop$.next(false);
    const initObject: UploadInitModel = {} as UploadInitModel;
    const file = this.checkIfUploadOnGoing();
    if (file) {
      if (file.uploadId.length > 0) {
        this.splitChunks(file);
      } else {
        const encryptedId = this.getEncryptedId(file.fileName);
        if (this.isEncrypted) {
          initObject.encryptedId = encryptedId;
        }
        if (file.objectId) {
          initObject.parentId = file.objectId;
          this.parentId = file.objectId;
        }
        initObject.fileName = this.isEncrypted ? encryptedId : file.fileName;
        initObject.extension = file.file.name.split('.').pop() ?? null;
        initObject.size = file.file.size;
        initObject.encrypted = this.isEncrypted;
        initObject.applicationName = 'storage';
        initObject.chunkCount = Math.ceil(file.file.size / this.CHUNK_SIZE);
        this.fileUploadService.multiPartInit(initObject).subscribe({
          next: (res) => {
            if (res.id) {
              file.uploadId = res.id;
              this.splitFileIntoChunks(file, initObject).then((chunks) => {
                file.data = chunks;
                this.splitChunks(file);
              });
            }
          },
          error: (error) => {
            file.status = 'cancelled';
            this.uploadInit();
            this.hasProcessedDrop$.next(false);
          },
        });
      }
    }
  }

  splitChunks(file: UploadModel): void {
    const pendingChunks = file.data.filter((data) => data.status === 'pending');
    if (pendingChunks.length > 0) {
      const startIndex = pendingChunks.sort((a, b) => a.chunkIndex - b.chunkIndex)[0].chunkIndex - 1;
      if (file.status === 'uploading') {
        const numberOfChunks = startIndex + this.NUMBER_OF_CHUNKS > file.data.length ? file.data.length : startIndex + this.NUMBER_OF_CHUNKS;
        this.processChunks(file, startIndex, numberOfChunks);
      }
    }
  }

  processChunks(file: UploadModel, startIndex: number, chunkCount: number): void {
    const slicedData: ChunkModel[] = file.data.slice(startIndex, chunkCount);
    if (file.status === 'uploading') {
      slicedData.forEach((data) => {
        if (data.status === 'pending') {
          this.sendChunkToServer(file, data);
        }
      });
    }
  }

  sendChunkToServer(file: UploadModel, chunk: ChunkModel): void {
    this.uploadSubscriptions[file.uploadId] = new Subscription();
    const storeObject: UploadStoreModel = {} as UploadStoreModel;
    const arrIndex = this.uploadFiles.findIndex((x) => x.uploadId === file.uploadId);
    if (chunk.status === 'pending' && arrIndex !== -1) {
      this.uploadFiles[arrIndex].data[chunk.chunkIndex - 1].status = 'uploading';
      storeObject.uploadId = file.uploadId;
      storeObject.chunkIndex = chunk.chunkIndex;
      if (this.isEncrypted) {
        this.blobToUint8Array(chunk.data).then((uint8Array) => {
          SgeCrypto.encryptFileChunk(storeObject.uploadId, uint8Array, storeObject.chunkIndex, this.encryptCallback.bind(this));
        });
      } else {
        this.uploadChunk(storeObject, file, chunk, arrIndex);
      }
    }
  }

  uploadChunk(storeObject: UploadStoreModel, file: UploadModel, chunk: ChunkModel, arrIndex: number): void {
    this.uploadSubscriptions[file.uploadId].add(
      this.fileUploadService.multiPartStore(storeObject, chunk.data).subscribe({
        next: (res) => {
          if (this.uploadFiles[arrIndex] && res.loaded && res.total) {
            this.uploadFiles[arrIndex].data[chunk.chunkIndex - 1].progress = Math.round((res.loaded / res.total) * 100);
          }
        },
        error: () => {
          if (chunk.status !== 'paused') {
            this.uploadFiles[arrIndex].status = 'failed';
            this.hasProcessedDrop$.next(false);
          }
        },
        complete: () => {
          if (this.uploadFiles[arrIndex]) {
            this.uploadFiles[arrIndex].data[chunk.chunkIndex - 1].status = 'completed';
            this.checkUploadProgress(file, arrIndex);
          }
        },
      }),
    );
  }

  async blobToUint8Array(blob: Blob): Promise<Uint8Array> {
    const arrayBuffer = await blob.arrayBuffer();

    return new Uint8Array(arrayBuffer);
  }

  checkUploadProgress(file: UploadModel, arrIndex: number): void {
    let slicedData: ChunkModel = {} as ChunkModel;
    if (this.uploadFiles[arrIndex] && this.uploadFiles[arrIndex].status !== 'paused') {
      this.getUploadedChunks(arrIndex).then((count) => {
        if (count !== this.uploadFiles[arrIndex].data.length) {
          [slicedData] = this.uploadFiles[arrIndex].data.filter((x) => x.status === 'pending');
          if (slicedData && Object.keys(slicedData).length > 0) {
            this.sendChunkToServer(file, slicedData);
          }
        } else {
          if (this.uploadFiles[arrIndex].status !== 'completed') {
            this.uploadFiles[arrIndex].status = 'completed';
            this.checkUploadStatus(this.uploadFiles[arrIndex].uploadId, arrIndex);
          }
        }
      });
    }
  }

  checkUploadStatus(id: string, arrIndex: number): void {
    new Promise<boolean>((resolve) => {
      const uploadCompleted = this.uploadFiles[arrIndex].data.every((chunk) => chunk.status === 'completed');
      resolve(uploadCompleted);
    }).then((isCompleted: boolean) => {
      if (this.uploadFiles[arrIndex].status !== 'uploaded' && isCompleted) {
        this.completeSubscription.add(
          this.fileUploadService.multiPartComplete(id, this.parentId!).subscribe({
            next: (res) => {
              if (res.completed) {
                SgeCrypto.finishFileEncryption(id);
                this.uploadFiles[arrIndex].status = 'uploaded';
                this.uploadItems$.next(this.uploadFiles);
                this.uploadCompleted$.next(true);
                if (this.objectId === this.uploadFiles[arrIndex].objectId) {
                  this.functionTrigger$.next();
                }
                this.uploadInit();
              } else {
                new Promise<void>((resolve) => {
                  this.uploadFiles[arrIndex].status = 'pending';
                  res.missingChunks.forEach((chunk) => {
                    this.uploadFiles[arrIndex].data[chunk - 1].status = 'pending';
                    this.uploadFiles[arrIndex].data[chunk - 1].progress = 0;
                  });
                  resolve();
                }).then(() => {
                  this.uploadInit();
                });
              }
            },
          }),
        );
      }
    });
  }

  getUploadedChunks(index: number): Promise<number> {
    return new Promise((resolve) => {
      const numberOfChunksUploaded = this.uploadFiles[index].data.filter((chunk) => chunk.status === 'completed').length;
      resolve(numberOfChunksUploaded);
    });
  }

  cancelUploads(): void {
    this.uploadFiles.forEach((chunk) => {
      this.unsubscribeUpload(chunk.uploadId);
    });
    this.uploadFiles = [];
    this.hasProcessedDrop$.next(false);
    this.uploadItems$.next(this.uploadFiles);
  }

  cancelUpload(uploadId: string): void {
    const index = this.uploadFiles.findIndex((chunk) => chunk.uploadId === uploadId);
    if (index !== -1) {
      this.uploadFiles.splice(index, 1);
      this.uploadInit();
    }
    this.unsubscribeUpload(uploadId);
    this.hasProcessedDrop$.next(false);
    this.uploadItems$.next(this.uploadFiles);
  }

  pauseUpload(uploadId: string): void {
    const index = this.uploadFiles.findIndex((chunk) => chunk.uploadId === uploadId);
    if (index !== -1) {
      this.uploadFiles[index].status = 'paused';
    }
    this.uploadFiles[index].data = this.uploadFiles[index].data.map((element) => {
      if (element.status === 'uploading') {
        element.status = 'pending';
      }

      return element;
    });
    this.uploadInit();
    this.unsubscribeUpload(uploadId);
    this.hasProcessedDrop$.next(false);
  }

  resumeUpload(uploadId: string): void {
    const index = this.uploadFiles.findIndex((chunk) => chunk.uploadId === uploadId);
    if (index !== -1) {
      this.uploadFiles[index].status = 'pending';
    }
    this.uploadInit();
    this.hasProcessedDrop$.next(false);
  }

  retryUploads(): void {
    const failedUploads = this.uploadFiles.filter((chunk) => chunk.status === 'failed');
    failedUploads.forEach((chunk) => {
      chunk.status = 'pending';
    });
    this.uploadInit();
    this.hasProcessedDrop$.next(false);
  }

  retryUpload(uploadId: string): void {
    const index = this.uploadFiles.findIndex((chunk) => chunk.uploadId === uploadId);
    if (index !== -1) {
      this.uploadFiles[index].status = 'pending';
    }
    this.uploadInit();
    this.hasProcessedDrop$.next(false);
  }

  unsubscribeUpload(uploadId: string): void {
    if (this.uploadSubscriptions[uploadId]) {
      this.uploadSubscriptions[uploadId].unsubscribe();
    }
  }

  private splitFileIntoChunks(file: UploadModel, initObject: UploadInitModel): Promise<ChunkModel[]> {
    const encryptedParentId = file.encryptedParentId ?? this.encryptedParentId;
    const promise = new Promise<ChunkModel[]>((resolve) => {
      const fileSize = file.file.size;
      const chunks: ChunkModel[] = [];
      let offset = 0;
      let index = 1;

      while (offset < fileSize) {
        const chunk = file.file.slice(offset, offset + this.CHUNK_SIZE);
        chunks.push({ data: chunk, chunkIndex: index, status: 'pending', progress: 0, remainingTime: 0 });
        offset += this.CHUNK_SIZE;
        index++;
      }

      const encryptionOpt: FileEncryptionOptions = {
        chunkCount: chunks.length,
        chunkByteLength: this.CHUNK_SIZE,
        fileByteLength: fileSize,
        fileName: file.fileName,
        encryptName: this.isEncrypted,
      };

      if (this.isEncrypted && !this.existingFile) {
        SgeCrypto.startNewFileEncryption(file.uploadId, encryptedParentId, initObject.encryptedId, encryptionOpt).then(() => {
          resolve(chunks);
        });
      } else if (this.isEncrypted && this.existingFile) {
        SgeCrypto.startExistingFileEncryption(file.uploadId, this.existingFile.encryptedId, encryptionOpt).then(() => {
          resolve(chunks);
        });
      } else {
        resolve(chunks);
      }
    });

    return promise;
  }

  private getEncryptedId(fileName: string): string {
    const file = this.filesAndFolders.filter((x) => x.objectName === fileName);
    if (file.length > 0) {
      [this.existingFile] = file;

      return file[0].encryptedId;
    } else {
      return SgeCrypto.generateId();
    }
  }

  private encryptCallback(uploadId: string, chunkNumber: number, encryptedChunkData: Uint8Array): void {
    const storeObject: UploadStoreModel = {
      uploadId,
      chunkIndex: chunkNumber,
    };
    const file = this.uploadFiles.find((x) => x.uploadId === uploadId);
    if (file) {
      file.data[chunkNumber - 1].data = new Blob([encryptedChunkData]);
      const arrIndex = this.uploadFiles.findIndex((x) => x.uploadId === uploadId);
      this.uploadChunk(storeObject, file, file.data[chunkNumber - 1], arrIndex);
    }
  }

  setEncryptedId(id: string | null): void {
    this.encryptedId.next(id);
  }

  setObjectId(id: string | null): void {
    this.objectIdSubject.next(id);
  }

  formatRemainingTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    return `${minutes} dk ${remainingSeconds} sn`;
  }
}
